import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Link2, Plus, Upload, X } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseDocument } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineAlert,
  LoadingState,
  ProgressBar,
  StatusBadge,
} from "../../shared/ui";
import { DocumentDrawer } from "./DocumentDrawer";
import { ReleaseNotesCard } from "./ReleaseNotesCard";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * What the release must produce, and what it has (§42).
 *
 * <p>Requirements and documents are one list rather than two, because they are the same question
 * at two moments. A row without a file is what the release still owes; the same row with one is
 * the document. Two lists would leave somebody comparing them to work out what is outstanding —
 * which is exactly the sum the readiness gate is there to do.
 */
export function ReleaseDocumentsTab({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [drawer, setDrawer] = useState<{ mode: "require" | "link" } | null>(null);
  const [uploadingInto, setUploadingInto] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const pendingType = useRef<string | null>(null);
  const { run, error, saving } = useReleaseMutation(release.id);

  const documents = useQuery({
    queryKey: ["release-documents", release.id],
    queryFn: () => api.get<ReleaseDocument[]>(`/api/v1/releases/${release.id}/documents`),
  });

  const rows = documents.data ?? [];
  const required = rows.filter((row) => row.mandatory);
  const provided = required.filter((row) => row.provided).length;

  const chooseFile = (documentType: string) => {
    pendingType.current = documentType;
    fileInput.current?.click();
  };

  const onFileChosen = (file: File | undefined) => {
    const documentType = pendingType.current;
    if (!file || !documentType) {
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setUploadingInto(documentType);
    void run(
      () =>
        api.upload(
          `/api/v1/releases/${release.id}/documents/upload?documentType=${documentType}`,
          form,
        ),
      onChanged,
    ).finally(() => setUploadingInto(null));
    if (fileInput.current) {
      fileInput.current.value = "";
    }
  };

  const withdraw = (document: ReleaseDocument) =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/documents/${document.id}/withdraw`),
      onChanged,
    );

  const remove = (document: ReleaseDocument) =>
    void run(
      () => api.del(`/api/v1/releases/${release.id}/documents/${document.id}`),
      onChanged,
    );

  return (
    <div className="space-y-4">
      <input
        ref={fileInput}
        type="file"
        className="hidden"
        onChange={(event) => onFileChosen(event.target.files?.[0])}
      />

      <Card>
        <CardHeader
          title="Documents"
          subtitle={
            required.length === 0
              ? "Nothing required of this release."
              : `${provided} of ${required.length} required documents attached`
          }
          action={
            can("RELEASE_EDIT") &&
            rows.length > 0 && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Link2 className="h-4 w-4" />}
                  onClick={() => setDrawer({ mode: "link" })}
                >
                  Link one
                </Button>
                <Button
                  size="sm"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setDrawer({ mode: "require" })}
                >
                  Require one
                </Button>
              </div>
            )
          }
        />
        <CardBody className="space-y-3">
          {error && <InlineAlert tone="error">{error}</InlineAlert>}

          {required.length > 0 && (
            <ProgressBar
              value={provided}
              total={required.length}
              tone={provided === required.length ? "green" : "amber"}
              label="Required documents"
            />
          )}

          {documents.isPending ? (
            <LoadingState label="Loading documents…" />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No documents recorded"
              message="A deployment plan, a rollback plan, a signed CAB approval. Saying which are required is what lets the release say whether it has them."
              action={
                can("RELEASE_EDIT") ? (
                  <div className="flex gap-2">
                    <Button onClick={() => setDrawer({ mode: "require" })}>
                      Require a document
                    </Button>
                    <Button variant="secondary" onClick={() => setDrawer({ mode: "link" })}>
                      Link one
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {rows.map((row) => (
                <li key={row.id} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="flex min-w-0 gap-2.5">
                    <FileText
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        row.provided ? "text-green-600" : "text-ink-300"
                      }`}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-900">
                        {row.title ?? row.label}
                        {!row.mandatory && (
                          <span className="ml-2 text-xs font-normal text-ink-500">optional</span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-500">
                        {row.label}
                        {row.providedBy && ` · ${row.providedBy.fullName}`}
                        {row.fileName && ` · ${row.fileName}`}
                      </p>
                      {row.externalUrl && (
                        <a
                          href={row.externalUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-0.5 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
                        >
                          <Link2 className="h-3 w-3" aria-hidden />
                          {row.externalUrl}
                        </a>
                      )}
                      {row.notes && <p className="mt-1 text-xs text-ink-600">{row.notes}</p>}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge tone={row.provided ? "green" : row.mandatory ? "red" : "gray"}>
                      {row.provided ? "Attached" : row.mandatory ? "Missing" : "Not attached"}
                    </StatusBadge>

                    {row.attachmentId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Download className="h-4 w-4" />}
                        aria-label={`Download ${row.fileName ?? row.label}`}
                        onClick={() =>
                          void api.download(
                            `/api/v1/attachments/${row.attachmentId}/content`,
                            row.fileName ?? row.label,
                          )
                        }
                      />
                    )}

                    {can("RELEASE_DOCUMENT_UPLOAD") && !row.provided && (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Upload className="h-4 w-4" />}
                        loading={uploadingInto === row.documentType}
                        onClick={() => chooseFile(row.documentType)}
                      >
                        Upload
                      </Button>
                    )}

                    {can("RELEASE_DOCUMENT_UPLOAD") && row.provided && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Remove the document, keeping the requirement"
                        onClick={() => withdraw(row)}
                      >
                        Remove file
                      </Button>
                    )}

                    {can("RELEASE_EDIT") && !row.mandatory && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={saving}
                        icon={<X className="h-4 w-4" />}
                        aria-label={`Remove ${row.label}`}
                        onClick={() => remove(row)}
                      />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <ReleaseNotesCard release={release} onChanged={onChanged} />

      <DocumentDrawer
        release={release}
        mode={drawer?.mode ?? "require"}
        open={drawer != null}
        onClose={() => setDrawer(null)}
        onSaved={onChanged}
      />
    </div>
  );
}
